import { useEffect } from "react";

// PDF.js keeps the same <canvas> mounted across page changes and repaints it in place,
// so there's no DOM add/remove to observe. Two signals tell us a real repaint happened:
// 1. Setting canvas.width/height (even to the same value) always clears its bitmap per the
//    Canvas spec, so a width/height attribute mutation marks "a new render just started".
// 2. Sampling pixel content afterward confirms the new page has actually been drawn — we
//    only trust that sample once signal 1 has fired, so we never mistake the *previous*
//    page's leftover pixels (still on the canvas until PDF.js clears it) for "done".
const BLANK_BRIGHTNESS_THRESHOLD = 253;
const MIN_LOADING_MS = 80;
const PAINT_WAIT_TIMEOUT_MS = 2000;
const SAMPLE_SIZE = 32;
const SAMPLE_POINTS: Array<[number, number]> = [
  [0.5, 0.5],
  [0.25, 0.25],
  [0.75, 0.25],
  [0.25, 0.75],
  [0.75, 0.75],
];

// Darkest of several sampled regions, so a page with an empty center doesn't read as blank.
function sampleContentBrightness(canvas: HTMLCanvasElement): number {
  try {
    const ctx = canvas.getContext("2d");
    if (!ctx || canvas.width === 0 || canvas.height === 0) return 255;
    let darkest = 255;
    for (const [fx, fy] of SAMPLE_POINTS) {
      const w = Math.min(SAMPLE_SIZE, canvas.width);
      const h = Math.min(SAMPLE_SIZE, canvas.height);
      const x = Math.max(0, Math.min(canvas.width - w, Math.floor(canvas.width * fx - w / 2)));
      const y = Math.max(0, Math.min(canvas.height - h, Math.floor(canvas.height * fy - h / 2)));
      const data = ctx.getImageData(x, y, w, h).data;
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) sum += (data[i] + data[i + 1] + data[i + 2]) / 3;
      const avg = sum / (data.length / 4);
      if (avg < darkest) darkest = avg;
    }
    return darkest;
  } catch {
    return 255;
  }
}

/**
 * Toggles a `pdfPageLoading` class on `#pdf-page-wrapper` while PDF.js repaints the page
 * canvas between page changes, so the CSS spinner in styles/index.js can mask the blank gap.
 */
export function usePageTransitionSpinner() {
  useEffect(() => {
    let rafId: number | undefined;
    // Bumped on every page change so a superseded waiter (from rapid navigation) can tell
    // it's stale and stop instead of clearing the class for a transition that already moved on.
    let generation = 0;
    let resizeSeenForGeneration = -1;

    function waitForPaint(canvas: HTMLCanvasElement, wrapper: Element, myGeneration: number) {
      const start = performance.now();
      let consecutiveReady = 0;

      const tick = () => {
        if (myGeneration !== generation) return;

        const elapsed = performance.now() - start;
        const canvasWasCleared = resizeSeenForGeneration === myGeneration;
        const painted = canvasWasCleared && sampleContentBrightness(canvas) < BLANK_BRIGHTNESS_THRESHOLD;
        const ready = elapsed >= MIN_LOADING_MS && painted;
        consecutiveReady = ready ? consecutiveReady + 1 : 0;

        if (consecutiveReady >= 2 || elapsed > PAINT_WAIT_TIMEOUT_MS) {
          wrapper.classList.remove("pdfPageLoading");
          return;
        }
        rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }

    const observer = new MutationObserver((mutations) => {
      // Two passes: page-number changes must bump `generation` before any width/height
      // mutation in the same batch is attributed to it, regardless of array order.
      for (const mutation of mutations) {
        if (mutation.type !== "attributes" || mutation.attributeName !== "data-page-number") continue;
        const pageEl = mutation.target as HTMLElement;
        const wrapper = pageEl.closest("#pdf-page-wrapper");
        const canvas = pageEl.querySelector("canvas");
        if (!wrapper || !canvas) continue;
        generation += 1;
        wrapper.classList.add("pdfPageLoading");
        waitForPaint(canvas, wrapper, generation);
      }

      for (const mutation of mutations) {
        if (mutation.type !== "attributes") continue;
        if (mutation.attributeName !== "width" && mutation.attributeName !== "height") continue;
        if (mutation.target instanceof HTMLCanvasElement) {
          resizeSeenForGeneration = generation;
        }
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-page-number", "width", "height"],
      subtree: true,
    });

    return () => {
      observer.disconnect();
      generation += 1; // invalidate any in-flight waiter
      if (rafId !== undefined) cancelAnimationFrame(rafId);
    };
  }, []);
}
